import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { LocalStorageService } from 'ngx-localstorage';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { appCommonConstants } from '../app.constants';
import { IContainerAlertMessage } from '../classes/appContainerDefault.class';
import { TokenApiModel } from '../classes/tokenApiModel.class';
import { IAppLoginDataType } from '../interfaces/auth.interfaces';
import { IAppHttpErrorResponse, IFormatDescriptionErrorDataType } from '../interfaces/httpError.interfaces';
import { appAuthConstants } from '../modules/auth/auth.constants';
import { appTraceConstantsUrl } from '../modules/trace/trace.constants';
import { AppAuthService } from '../services/auth.service';
import { AppAuthDataService } from '../services/authData.service';
import { AppEventService } from '../services/event.service';
import { AppLoggerService } from '../services/logger.service';
import { AppSignalRService } from '../services/signalR.service';

@Injectable({
  providedIn: 'root',
})

/**
 * Interceptor per la gestione dell'access token e refresh token
 * Basato su https://github.com/bartosz-io/jwt-auth-angular/
 */
export class AppHttpInterceptor implements HttpInterceptor {
  constructor(
    private authDataService: AppAuthDataService,
    private authService: AppAuthService,
    private eventService: AppEventService,
    private localStorage: LocalStorageService,
    private logger: AppLoggerService,
    private signalrService: AppSignalRService,
    private translate: TranslateService) {
  }

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    if (req.url !== appTraceConstantsUrl.systemLogMessage.getNoAckCount && req.url !== appAuthConstants.url.verify) {  // Esclusione del caso di ricerca numero messaggi da leggere (metodo NON usato in questo progetto) o verify
      this.eventService.broadcast(appCommonConstants.events.containerAlert, null);
    }

    let authReq: HttpRequest<any>;

    let loginData = this.localStorage.get(appCommonConstants.localStorage.loginData) as IAppLoginDataType;

    // Clone dell'header con impostazione di header custom AppName
    let headers = req.headers.set("AppName", environment.appSettings.applicationName);

    if (loginData) {
      if (loginData.asNoFilter != null) {
        headers = headers.append("AsNoFilter", loginData.asNoFilter.toString());
      }
      if (loginData.environment != null) {
        headers = headers.append("Environment", loginData.environment);
      }
      if (this.signalrService?.isConnected) {
        const connectionId = this.signalrService?.connection?.connectionId;
        if (connectionId) {
          headers = headers.append("SignalrConnectionId", connectionId);
        }
      }
    }

    {
      // Blocco valorizzazione token di autenticazione (Bearer)
      let authToken: string | undefined;
      if (environment.azureLogin?.enabled && loginData?.azureLogin) {  // Retrieve del token Azure
        // Ci pensa MsalSkipInterceptor a valorizzare l'header Authorization
        authToken = req.headers.get("Authorization")?.replace(/Bearer/ig, "")?.trim();
        if (authToken) {
          // Salvataggio del token Azure in authDataService per eventuale utilizzo in altri contesti (es. SignalR)
          this.authDataService.setAuthorizationToken(authToken);
        }
      } else {
        authToken = this.authDataService.getAuthorizationToken();
        /**
         * Controllo valorizzazione authToken - Ref: https://github.com/angular/angular/issues/18743#issuecomment-323215006
         * Aggiunge l'header "Authorization" se il token è assegnato e l'header non è già stato impostato
         */
        if (authToken != null && req.headers.get("Authorization") == null) {
          headers = headers.append("Authorization", `Bearer ${authToken}`);
        }
      }
    }

    if (req.headers.get("Content-Type") == null && !(req.body instanceof FormData)) {
      headers = headers.append("Content-Type", "application/json");
    } else if (req.body instanceof FormData) {  // Rimozione Content-Type in caso di FormData (ci pensa il browser a settare il valore corretto)
      headers = headers.delete("Content-Type");
    }

    authReq = req.clone({ headers: headers });

    /**
     * Send cloned request with header to the next handler.
     * Ref: https://stackoverflow.com/a/46021824
     */
    return next.handle(authReq).pipe(
      catchError((err: any) => {
        let errorDescription: IFormatDescriptionErrorDataType = this.formatDescriptionError(err);
        let errMsg: any = err;
        errMsg.description = errorDescription.description || err.message;
        errMsg.descriptionHtml = errorDescription.descriptionHtml || err.message;

        if (err.status !== 401 && window?.location?.hash?.replace("#", "") !== appCommonConstants.defaultRoutes.login) { // Visualizzazione messaggio solo se non alla login
          let message: string;
          if (err.status === 403) {
            message = this.translate.instant("MESSAGE.Operazione non ammessa: l'utente non dispone dei diritti necessari");
            errMsg.descriptionHtml = `<i class="fas fa-ban text-danger"></i>&nbsp;${message}`;
          } else {
            message = errMsg.descriptionHtml ?? errMsg.description;
          }

          this.logger.error({ message: message });
          if (req.url !== appTraceConstantsUrl.systemLogMessage.getNoAckCount) {  // Esclusione del caso di ricerca numero messaggi da leggere (metodo NON usato in questo progetto)
            this.eventService.broadcast(appCommonConstants.events.containerAlert, { dismissible: true, message: message, type: "danger" } as IContainerAlertMessage);
          }
        }

        if (err instanceof HttpErrorResponse) {
          if (err.status === 401) {
            // refreshToken
            return this.handleUnAuthorizedError(authReq, next);
          } else {
            return throwError(() => errMsg);
          }
        }
        return throwError(() => errMsg);
      }));
  }

  handleUnAuthorizedError(req: HttpRequest<any>, next: HttpHandler) {
    const authToken = this.authDataService.getAuthorizationToken();
    const refreshToken = this.authDataService.getRefreshToken();
    const tokenapimodel = new TokenApiModel(authToken || "", refreshToken || "", 60);

    if (!this.isRefreshing) {
      if (!tokenapimodel.access_token || !tokenapimodel.refresh_token) {  // Per evitare visualizzazione di inutili/fuorvianti messaggi di errore alla login
        this.logger.console.error(`handleUnAuthorizedError - authToken or refreshToken null or empty`);
        return next.handle(req);
      }
      return this.authService.refreshToken(tokenapimodel).pipe(
        switchMap((data: TokenApiModel) => {
          this.authDataService.setAuthorizationToken(data.access_token);
          this.authDataService.setRefreshToken(data.refresh_token);
          this.refreshTokenSubject.next(data.access_token);

          this.isRefreshing = false;

          req = req.clone({
            setHeaders: { Authorization: `bearer ${data.access_token}` }
          });
          return next.handle(req);
        }), catchError((err) => {
          this.authService.endlogout();
          this.logger.console.error(err);
          this.logger.console.error(`${err.status}: ${err.statusText}`, err);

          return throwError(() => "TokenExpired");
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(access_token => {
          req = req.clone({
            setHeaders: { Authorization: `bearer ${access_token}` }
          });
          return next.handle(req);
        }));
    }
  }

  private formatDescriptionError(errorResponse: IAppHttpErrorResponse): IFormatDescriptionErrorDataType {
    let returnMessage: string = "";
    let returnMessageHtml: string = "";

    let stackTraceParse = function (stackTrace: any, level: number, list: any[]) {
      if (!stackTrace) {
        return list;
      }
      stackTrace.forEach(function (e: any) {
        let exception = {
          Code: e.Code ?? e.code,
          Data: e.Data ?? e.data,
          Message: e.Message ?? e.message,
          Level: level,
        };
        list.push(exception);

        if (e.StackTrace) {
          // Livelli innestati
          stackTraceParse(e.StackTrace, level + 1, list);
        }
      });
      return list;
    }

    try {
      if (errorResponse.error?.content?.stackTrace?.length) {
        let flatStackTrace = stackTraceParse(errorResponse.error.content.stackTrace, 1, []);
        let message = flatStackTrace[0].Message;
        returnMessageHtml = message;

        // Tutto l'errore
        for (let i = 1; i < _.size(flatStackTrace); i++) {
          if (i === 1) {
            returnMessageHtml += `<ul style="margin: 0.5rem 0 0; padding-inline-start:inherit;">`;
          }
          returnMessageHtml += `<li>${flatStackTrace[i].Message}</li>`;
        }

        if (_.size(flatStackTrace) > 1) {
          returnMessageHtml += `</ul>`;
        }

        returnMessage = message;
      } else {
        returnMessage = errorResponse.error.message || errorResponse.message;
      }
    } catch (e) {
      returnMessage = "Internal error";
    }

    if (!returnMessageHtml) {
      returnMessageHtml = returnMessage;
    }

    return {
      description: returnMessage,
      descriptionHtml: returnMessageHtml
    };
  }
}
